import React, { useRef, useState, useEffect } from 'react';
import patientFieldConfigService from '../../services/patientFieldConfigService';
import { SummaryTypeKind, isTitrationKind } from '../../services/summaryTypeService';
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Chip,
  Paper,
  Divider,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  DragIndicator as DragIndicatorIcon,
  Image as ImageIcon,
  CalendarToday,
  Title,
  TextFields,
  ListAlt,
  Medication,
  LocalHospital,
  Draw,
  TableChart,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── Types ────────────────────────────────────────────────────────────────────

type Alignment = 'left' | 'center' | 'right';

interface LogoBlock    { type: 'logo';             alignment?: Alignment }
interface DateBlock    { type: 'date';             alignment?: Alignment }
interface TitleBlock   { type: 'title';            text?: string; bold?: boolean; underline?: boolean; fontSize?: number; alignment?: Alignment }
interface TextBlock    { type: 'text';             text?: string; bold?: boolean; underline?: boolean; fontSize?: number; alignment?: Alignment }
interface SummaryBlock { type: 'summary_sections'; name?: string; title?: string }
interface MedsBlock    { type: 'medications';      title?: string }
interface DiagBlock    { type: 'diagnostics';      title?: string }
interface TitrationScheduleBlock { type: 'titration_schedule'; title?: string }
interface SigBlock     { type: 'signature' }

/** `show_if` names a variable; the backend renders the block only when it has a value. */
type PdfBlock = (LogoBlock | DateBlock | TitleBlock | TextBlock | SummaryBlock | MedsBlock | DiagBlock | TitrationScheduleBlock | SigBlock) & { show_if?: string };

interface VariableOption { key: string; label: string }

const BLOCK_TYPES: { value: PdfBlock['type']; label: string; icon: React.ReactNode; titrationOnly?: boolean }[] = [
  { value: 'logo',             label: 'Logo',             icon: <ImageIcon fontSize="small" /> },
  { value: 'date',             label: 'Date',             icon: <CalendarToday fontSize="small" /> },
  { value: 'title',            label: 'Title',            icon: <Title fontSize="small" /> },
  { value: 'text',             label: 'Text',             icon: <TextFields fontSize="small" /> },
  { value: 'summary_sections', label: 'Summary Sections', icon: <ListAlt fontSize="small" /> },
  { value: 'medications',      label: 'Medications',      icon: <Medication fontSize="small" /> },
  { value: 'diagnostics',      label: 'Diagnostics',      icon: <LocalHospital fontSize="small" /> },
  { value: 'titration_schedule', label: 'Weekly Titration Schedule', icon: <TableChart fontSize="small" />, titrationOnly: true },
  { value: 'signature',        label: 'Signature',        icon: <Draw fontSize="small" /> },
];

const BLOCK_COLORS: Record<PdfBlock['type'], string> = {
  logo:             '#e3f2fd',
  date:             '#f3e5f5',
  title:            '#fff3e0',
  text:             '#f1f8e9',
  summary_sections: '#e8eaf6',
  medications:      '#fce4ec',
  diagnostics:      '#e0f7fa',
  titration_schedule: '#ede7f6',
  signature:        '#fafafa',
};

const BASE_PDF_VARIABLES: VariableOption[] = [
  { key: 'patient_name',       label: 'Full Name' },
  { key: 'patient_first_name', label: 'First Name' },
  { key: 'patient_last_name',  label: 'Last Name' },
  { key: 'dob',                label: 'Date of Birth' },
  { key: 'patient_id_number',  label: 'NHS / ID Number' },
  { key: 'patient_hm_id',      label: 'HM ID' },
  { key: 'patient_details',    label: 'Patient Details' },
  { key: 'registered_gp',      label: 'Registered GP' },
  { key: 'doctor_name',        label: 'Doctor' },
  { key: 'therapist_name',     label: 'Therapist (this meeting)' },
  { key: 'nurse_prescriber_name', label: 'Nurse Prescriber' },
  { key: 'case_manager_name', label: 'Case Manager' },
  { key: 'current_date',       label: 'Current Date' },
  { key: 'meeting_date',       label: 'Meeting Date' },
  { key: 'clinic_name',        label: 'Clinic' },
];

const TITRATION_VARIABLES: VariableOption[] = [
  { key: 'titration_medication_name',      label: 'Medication Name' },
  { key: 'titration_starting_dose',        label: 'Starting Dose' },
  { key: 'titration_dosage_instructions',  label: 'Dosage Instructions' },
  { key: 'titration_quantity',             label: 'Quantity' },
  { key: 'titration_how_to_take',          label: 'How to Take' },
  { key: 'titration_side_effects',         label: 'Side Effects' },
  { key: 'titration_comments',             label: 'Comments' },
];

const TITRATION_FOLLOWUP_VARIABLES: VariableOption[] = [
  { key: 'titration_baseline_asrs',             label: 'Baseline ASRS' },
  { key: 'titration_latest_asrs',               label: 'Latest ASRS' },
  { key: 'titration_adherence',                 label: 'Medication Adherence' },
  { key: 'titration_adherence_reason',          label: 'Non-Adherence Reason' },
  { key: 'titration_side_effect_severity',      label: 'Side Effects Severity' },
  { key: 'titration_blood_pressure',            label: 'Blood Pressure' },
  { key: 'titration_heart_rate',                label: 'Heart Rate' },
  { key: 'titration_weight',                    label: 'Weight' },
  { key: 'titration_bmi',                       label: 'BMI' },
  { key: 'titration_wellbeing_score',           label: 'Wellbeing Score' },
  { key: 'titration_treatment_decision',        label: 'Treatment Decision' },
  { key: 'titration_treatment_decision_options', label: 'Treatment Decision (checklist)' },
  { key: 'titration_additional_review_reasons', label: 'Additional Review Reasons' },
  { key: 'titration_additional_review_notes',   label: 'Additional Review Notes' },
  { key: 'titration_stop_treatment_reasons',    label: 'Stop Treatment Reasons' },
  { key: 'titration_stop_treatment_notes',      label: 'Stop Treatment Notes' },
];

const defaultBlock = (type: PdfBlock['type']): PdfBlock => {
  switch (type) {
    case 'logo':             return { type, alignment: 'right' };
    case 'date':             return { type, alignment: 'left' };
    case 'title':            return { type, text: '', bold: true, underline: false, fontSize: 16, alignment: 'left' };
    case 'text':             return { type, text: '', bold: false, underline: false, fontSize: 12, alignment: 'left' };
    case 'summary_sections': return { type, name: '', title: '' };
    case 'medications':      return { type, title: '' };
    case 'diagnostics':      return { type, title: '' };
    case 'titration_schedule': return { type, title: 'Weekly Titration Schedule' };
    case 'signature':        return { type };
  }
};

// ─── Shared field components ──────────────────────────────────────────────────

const AlignmentField: React.FC<{ value?: Alignment; onChange: (v: Alignment) => void }> = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>Alignment</Typography>
    <Select
      value={value || 'left'}
      size="small"
      sx={{ minWidth: 100 }}
      onChange={(e: SelectChangeEvent) => onChange(e.target.value as Alignment)}
    >
      <MenuItem value="left">Left</MenuItem>
      <MenuItem value="center">Center</MenuItem>
      <MenuItem value="right">Right</MenuItem>
    </Select>
  </Box>
);

// ─── Title block fields ───────────────────────────────────────────────────────

const TitleBlockFields: React.FC<{
  block: TitleBlock;
  onChange: (patch: Partial<TitleBlock>) => void;
}> = ({ block, onChange }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <TextField
      size="small"
      label="Text"
      value={block.text || ''}
      onChange={(e) => onChange({ text: e.target.value })}
      fullWidth
    />
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <FormControlLabel
        control={<Checkbox checked={!!block.bold} size="small" onChange={(e) => onChange({ bold: e.target.checked })} />}
        label={<Typography variant="caption">Bold</Typography>}
        sx={{ m: 0 }}
      />
      <FormControlLabel
        control={<Checkbox checked={!!block.underline} size="small" onChange={(e) => onChange({ underline: e.target.checked })} />}
        label={<Typography variant="caption">Underline</Typography>}
        sx={{ m: 0 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">Size</Typography>
        <TextField
          size="small"
          type="number"
          value={block.fontSize ?? 16}
          onChange={(e) => onChange({ fontSize: Number(e.target.value) })}
          sx={{ width: 64 }}
          inputProps={{ min: 8, max: 48, style: { padding: '4px 8px' } }}
        />
      </Box>
      <AlignmentField
        value={block.alignment || 'left'}
        onChange={(v) => onChange({ alignment: v })}
      />
    </Box>
  </Box>
);

// ─── Text block fields (with alignment + variable picker) ─────────────────────

const TextBlockFields: React.FC<{
  block: TextBlock;
  onChange: (patch: Partial<TextBlock>) => void;
  variables: VariableOption[];
}> = ({ block, onChange, variables }) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const insertVariable = (varKey: string) => {
    const token = `{${varKey}}`;
    const el = inputRef.current;
    if (!el) {
      onChange({ text: (block.text || '') + token });
      return;
    }
    const start = el.selectionStart ?? (block.text || '').length;
    const end   = el.selectionEnd   ?? start;
    const newText = (block.text || '').slice(0, start) + token + (block.text || '').slice(end);
    onChange({ text: newText });
    requestAnimationFrame(() => {
      el.selectionStart = start + token.length;
      el.selectionEnd   = start + token.length;
      el.focus();
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        size="small"
        label="Text"
        value={block.text || ''}
        onChange={(e) => onChange({ text: e.target.value })}
        fullWidth
        multiline
        minRows={3}
        inputRef={inputRef}
      />

      {/* Variable picker */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Insert:</Typography>
        {variables.map((v) => (
          <Tooltip key={v.key} title={`{${v.key}}`} placement="top">
            <Chip
              label={v.label}
              size="small"
              variant="outlined"
              onClick={() => insertVariable(v.key)}
              sx={{ fontSize: 10, height: 20, cursor: 'pointer' }}
            />
          </Tooltip>
        ))}
      </Box>

      {/* Style + alignment row */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Checkbox checked={!!block.bold} size="small" onChange={(e) => onChange({ bold: e.target.checked })} />}
          label={<Typography variant="caption">Bold</Typography>}
          sx={{ m: 0 }}
        />
        <FormControlLabel
          control={<Checkbox checked={!!block.underline} size="small" onChange={(e) => onChange({ underline: e.target.checked })} />}
          label={<Typography variant="caption">Underline</Typography>}
          sx={{ m: 0 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Size</Typography>
          <TextField
            size="small"
            type="number"
            value={block.fontSize ?? 12}
            onChange={(e) => onChange({ fontSize: Number(e.target.value) })}
            sx={{ width: 64 }}
            inputProps={{ min: 8, max: 48, style: { padding: '4px 8px' } }}
          />
        </Box>
        <AlignmentField
          value={block.alignment || 'left'}
          onChange={(v) => onChange({ alignment: v })}
        />
      </Box>
    </Box>
  );
};

// ─── Summary Sections block fields ───────────────────────────────────────────

const SummaryBlockFields: React.FC<{
  block: SummaryBlock;
  sectionNames: string[];
  onChange: (patch: Partial<SummaryBlock>) => void;
}> = ({ block, sectionNames, onChange }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    {/* Section name filter */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>Section</Typography>
      <Select
        value={block.name || ''}
        size="small"
        displayEmpty
        sx={{ flex: 1 }}
        onChange={(e: SelectChangeEvent) => onChange({ name: e.target.value })}
      >
        <MenuItem value=""><em>All sections</em></MenuItem>
        {sectionNames.map((n) => (
          <MenuItem key={n} value={n}>{n}</MenuItem>
        ))}
      </Select>
    </Box>

    {/* Custom title override */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>Title</Typography>
      <TextField
        size="small"
        value={block.title || ''}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder="Leave empty to use section label"
        sx={{ flex: 1 }}
      />
    </Box>
  </Box>
);

// ─── Generic title field (medications / diagnostics) ─────────────────────────

const TitleField: React.FC<{ value: string; onChange: (patch: object) => void }> = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>Title</Typography>
    <TextField
      size="small"
      value={value || ''}
      onChange={(e) => onChange({ title: e.target.value })}
      placeholder="Leave empty for default"
      sx={{ flex: 1 }}
    />
  </Box>
);

// ─── Conditional display (any block) ─────────────────────────────────────────

const ShowIfField: React.FC<{
  value?: string;
  variables: VariableOption[];
  onChange: (value: string | undefined) => void;
}> = ({ value, variables, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>Show</Typography>
    <Select
      value={value || ''}
      size="small"
      displayEmpty
      sx={{ flex: 1, fontSize: 12 }}
      onChange={(e: SelectChangeEvent) => onChange(e.target.value || undefined)}
    >
      <MenuItem value=""><em>Always</em></MenuItem>
      {/* Keep a saved condition selectable even if its variable is not offered here */}
      {value && !variables.some((v) => v.key === value) && (
        <MenuItem value={value}>{`Only if {${value}} has a value`}</MenuItem>
      )}
      {variables.map((v) => (
        <MenuItem key={v.key} value={v.key}>{`Only if "${v.label}" has a value`}</MenuItem>
      ))}
    </Select>
  </Box>
);

// ─── Single block card ─────────────────────────────────────────────────────────

interface BlockCardProps {
  id: string;
  block: PdfBlock;
  sectionNames: string[];
  variables: VariableOption[];
  onChange: (patch: Partial<PdfBlock>) => void;
  onDelete: () => void;
}

const BlockCard: React.FC<BlockCardProps> = ({ id, block, sectionNames, variables, onChange, onDelete }) => {
  const meta = BLOCK_TYPES.find((b) => b.value === block.type)!;
  const bg = BLOCK_COLORS[block.type];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  };

  return (
    <Paper ref={setNodeRef} style={style} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: bg }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{ cursor: 'grab', color: 'text.secondary', display: 'flex', alignItems: 'center', '&:active': { cursor: 'grabbing' } }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
          {meta.icon}
          <Chip label={meta.label} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
        </Box>
        <Button size="small" color="error" onClick={onDelete} sx={{ minWidth: 32, p: 0 }}><Delete fontSize="small" /></Button>
      </Box>

      {/* Fields */}
      {(block.type === 'logo' || block.type === 'date') && (
        <Box sx={{ px: 1.5, py: 1 }}>
          <AlignmentField value={(block as LogoBlock | DateBlock).alignment} onChange={(v) => onChange({ alignment: v } as any)} />
        </Box>
      )}

      {block.type === 'title' && (
        <Box sx={{ px: 1.5, py: 1 }}>
          <TitleBlockFields block={block as TitleBlock} onChange={(patch) => onChange(patch as any)} />
        </Box>
      )}

      {block.type === 'text' && (
        <Box sx={{ px: 1.5, py: 1 }}>
          <TextBlockFields block={block as TextBlock} onChange={(patch) => onChange(patch as any)} variables={variables} />
        </Box>
      )}

      {block.type === 'summary_sections' && (
        <Box sx={{ px: 1.5, py: 1 }}>
          <SummaryBlockFields
            block={block as SummaryBlock}
            sectionNames={sectionNames}
            onChange={(patch) => onChange(patch as any)}
          />
        </Box>
      )}

      {(block.type === 'medications' || block.type === 'diagnostics' || block.type === 'titration_schedule') && (
        <Box sx={{ px: 1.5, py: 1 }}>
          <TitleField value={(block as MedsBlock | DiagBlock | TitrationScheduleBlock).title || ''} onChange={(patch) => onChange(patch as any)} />
        </Box>
      )}

      {block.type === 'signature' && (
        <Box sx={{ px: 1.5, py: 0.75 }}>
          <Typography variant="caption" color="text.secondary">No additional settings</Typography>
        </Box>
      )}

      <Box sx={{ px: 1.5, pb: 1 }}>
        <ShowIfField value={block.show_if} variables={variables} onChange={(v) => onChange({ show_if: v })} />
      </Box>
    </Paper>
  );
};

// ─── Main editor ───────────────────────────────────────────────────────────────

interface PdfLayoutEditorProps {
  layout: any[];
  onChange: (layout: any[]) => void;
  sectionNames?: string[];
  summaryTypeType?: SummaryTypeKind;
  tenantId?: number;
}

const PdfLayoutEditor: React.FC<PdfLayoutEditorProps> = ({ layout, onChange, sectionNames = [], summaryTypeType = 'psychiatric', tenantId }) => {
  const blocks = layout as PdfBlock[];
  const isTitration = isTitrationKind(summaryTypeType);

  const [additionalFieldVars, setAdditionalFieldVars] = useState<VariableOption[]>([]);

  // Load the tenant's additional patient fields as insertable `af_<key>` variables
  useEffect(() => {
    if (!tenantId) return;
    patientFieldConfigService.getAll(tenantId)
      .then((cfgs) => {
        const seen = new Set<string>();
        const vars: VariableOption[] = [];
        for (const c of cfgs) {
          if (c.key && !seen.has(c.key)) {
            seen.add(c.key);
            vars.push({ key: `af_${c.key}`, label: c.label || c.key });
          }
        }
        setAdditionalFieldVars(vars);
      })
      .catch(() => {});
  }, [tenantId]);

  const variables: VariableOption[] = [
    ...BASE_PDF_VARIABLES,
    ...(isTitration ? TITRATION_VARIABLES : []),
    ...(summaryTypeType === 'titration_followup' ? TITRATION_FOLLOWUP_VARIABLES : []),
    ...additionalFieldVars,
  ];

  const blockTypes = BLOCK_TYPES.filter((b) => !b.titrationOnly || isTitration);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const updateBlock = (idx: number, patch: Partial<PdfBlock>) => {
    onChange(blocks.map((b, i) => i === idx ? { ...b, ...patch } : b));
  };

  const deleteBlock = (idx: number) => {
    onChange(blocks.filter((_, i) => i !== idx));
  };

  const addBlock = (type: PdfBlock['type']) => {
    onChange([...blocks, defaultBlock(type)]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onChange(arrayMove(blocks, Number(active.id), Number(over.id)));
    }
  };

  return (
    <Box>
      {blocks.length === 0 && (
        <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">No blocks yet — add one below</Typography>
        </Box>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
          {blocks.map((block, idx) => (
            <BlockCard
              key={idx}
              id={String(idx)}
              block={block}
              sectionNames={sectionNames}
              variables={variables}
              onChange={(patch) => updateBlock(idx, patch)}
              onDelete={() => deleteBlock(idx)}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {blockTypes.map(({ value, label, icon }) => (
          <Button
            key={value}
            size="small"
            variant="outlined"
            startIcon={icon}
            onClick={() => addBlock(value)}
            sx={{ fontSize: 11, textTransform: 'none' }}
          >
            {label}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default PdfLayoutEditor;
